import { useViewerStore } from '../../../../store/store';
import { captureLiveLayoutCardMetrics } from '../../../../helpers/aiDesignProductionSync';
import { applyCategoryBestLayoutsAction } from './dashboardActionCategoryBestVariants';
import { runGenerateAllCategoryLayoutVariantsAction } from './dashboardActionGenerateCategoryLayoutVariants';
import { runSimulateLayoutsForKeysAction } from './dashboardActionSimulateLayouts';
import { resolveRoofCombinationsForRunAi } from './utils/ai-layout';
import { restoreAllTrees } from '../../../../services/aiModuleFillingService';
import { beginBatchLayoutSiteCalc, endBatchLayoutSiteCalc } from './utils/layout';

type RunPostAiLayoutPipelineArgs = {
    sceneViewer: any;
    defaultMaxfillRoofIds: string[];
    bestRoofsForLayout: { roofFaceIds?: string[] } | null | undefined;
    selectedRoofCombinationKey: string;
    restoreDefaultLayoutFromSnapshot: () => Promise<void>;
    simulateLayoutForRoofIds: (roofFaceIds: string[]) => Promise<{
        production: number;
        modules: number;
        efficiency: number | null;
        weightedSolarAverage: number | null;
    }>;
    setSimulatingLayouts: (next: boolean) => void;
    setSimulatingLayoutIndex: (next: number) => void;
    setSimulatingLayoutTotal: (next: number) => void;
    setLayoutMetricsByKey: (next: (prev: Record<string, any>) => Record<string, any>) => void;
    setLayoutProductionByKey: (next: (prev: Record<string, number>) => Record<string, number>) => void;
    setLayoutModulesByKey: (next: (prev: Record<string, number>) => Record<string, number>) => void;
    setLayoutSimulationTimeByKey: (next: (prev: Record<string, number>) => Record<string, number>) => void;
    setAiLoadingStep: (next: string) => void;
    roofIdsByLayoutKey: (key: string) => string[];
};

/** Simulate Gemini roof combos, then pick the best main card layout per category from the full candidate pool. */
export async function runPostAiLayoutPipelineAction(args: RunPostAiLayoutPipelineArgs): Promise<void> {
    const {
        sceneViewer,
        defaultMaxfillRoofIds,
        bestRoofsForLayout,
        selectedRoofCombinationKey,
        restoreDefaultLayoutFromSnapshot,
        simulateLayoutForRoofIds,
        setSimulatingLayouts,
        setSimulatingLayoutIndex,
        setSimulatingLayoutTotal,
        setLayoutMetricsByKey,
        setLayoutProductionByKey,
        setLayoutModulesByKey,
        setLayoutSimulationTimeByKey,
        setAiLoadingStep,
        roofIdsByLayoutKey,
    } = args;

    const state = useViewerStore.getState() as any;
    const aiResult = typeof state.aiResult === 'string' ? state.aiResult : '';
    const weights = state.aiModuleFillingScenarios?.[0]?.shadeWeights ?? [];
    const validIds = new Set<string>(
        (weights || []).map((r: any) => r.roofFaceId).filter((id: unknown): id is string => typeof id === 'string'),
    );
    const combos = resolveRoofCombinationsForRunAi(aiResult, validIds, weights);
    useViewerStore.setState({ aiResolvedRoofCombinations: combos } as any);
    const keys = ['default', ...combos.map((c) => c.name).filter(Boolean)];
    const uniqueKeys = [...new Set(keys)];

    beginBatchLayoutSiteCalc();
    try {
        try {
            restoreAllTrees(sceneViewer);
        } catch {
            // Tree restore is best-effort so capture matches open-from-card (all trees visible).
        }
        if (uniqueKeys.length > 0) {
            setAiLoadingStep('Simulating layout options...');
            await runSimulateLayoutsForKeysAction({
                sceneViewer,
                keys: uniqueKeys,
                simulatingLayouts: false,
                defaultMaxfillRoofIds,
                bestRoofsForLayout,
                roofCombinationsFromAi: combos,
                selectedRoofCombinationKey,
                restoreDefaultLayoutFromSnapshot,
                simulateLayoutForRoofIds,
                setSimulatingLayouts,
                setSimulatingLayoutIndex,
                setSimulatingLayoutTotal,
                setLayoutMetricsByKey,
                setLayoutProductionByKey,
                setLayoutModulesByKey,
                setLayoutSimulationTimeByKey,
            });
        }

        // Ensure oneDRAW Default Maxfill (`default` key) has metrics even if the simulate pass skipped it.
        {
            const afterSim = useViewerStore.getState() as any;
            const defaultProd = afterSim.layoutProductionByKey?.default;
            if (!(typeof defaultProd === 'number' && Number.isFinite(defaultProd) && defaultProd > 0)) {
                await restoreDefaultLayoutFromSnapshot();
                const live = captureLiveLayoutCardMetrics();
                const captured = useViewerStore.getState() as any;
                const roofInfoArr = (captured.roofInfoArr as any[]) || [];
                const roofsWithModules = roofInfoArr.filter((r: any) => Number(r.modules ?? 0) > 0);
                setLayoutProductionByKey((prev) => ({ ...prev, default: live.production }));
                setLayoutModulesByKey((prev) => ({ ...prev, default: live.modules }));
                setLayoutMetricsByKey((prev) => ({
                    ...prev,
                    default: {
                        efficiency: live.efficiency,
                        solarAccess: live.solarAccess,
                        treeCount: null,
                        roofCount: roofsWithModules.length || defaultMaxfillRoofIds.length || null,
                        multiplier: live.multiplier,
                    },
                }));
                try {
                    const img = sceneViewer?.getSquareImageOfSite?.();
                    if (img) {
                        useViewerStore.setState((prev: any) => ({
                            layoutSnapshotsByKey: { ...(prev.layoutSnapshotsByKey || {}), default: img },
                        }));
                    }
                } catch {
                    // ignore snapshot errors
                }
            }
        }

        await runGenerateAllCategoryLayoutVariantsAction({
            sceneViewer,
            roofCombinationsFromAi: combos,
            defaultMaxfillRoofIds,
            bestRoofsForLayout: bestRoofsForLayout ?? {},
            selectedRoofCombinationKey,
            setAiLoadingStep,
        });

        applyCategoryBestLayoutsAction({ setAiLoadingStep, roofIdsByLayoutKey, roofCombinationsFromAi: combos });
    } finally {
        await endBatchLayoutSiteCalc(sceneViewer);
    }
}
